import React, { useMemo, useState } from 'react';
import {
  Autocomplete, Box, Button, IconButton, MenuItem, Stack, TextField, Typography,
} from '@mui/material';
import AddOutlinedIcon from '@mui/icons-material/AddOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import useFetch from '../../../../services/useFetch.js';
import { UoMMenuOptions } from '../../../Helpers/UoMMenuOptions.js';
import { tokens } from '../../../../theme';

const emptyRow = (customSize) => ({
  name: '',
  variant: null,
  menuId: null,
  posId: null,
  menuUoM: '',
  menuAmount: '',
  units: [{ amount: null, measurement: customSize || '' }],
});

function optionLabel(item) {
  if (!item) return '';
  const variant = item.posMenuItemVariant || 'Modifier';
  return `${item.posMenuItem} — ${variant}`;
}

function normalize(list, customSize) {
  if (!Array.isArray(list) || list.length === 0) return [];
  return list.map((m) => ({
    name: m.name || '',
    variant: m.variant ?? null,
    menuId: m.menuId ?? null,
    posId: m.posId ?? null,
    menuUoM: m.menuUoM || m.units?.[0]?.measurement || '',
    menuAmount: m.menuAmount ?? m.units?.[0]?.amount ?? '',
    units: [{
      amount: m.units?.[0]?.amount ?? m.menuAmount ?? null,
      measurement: m.units?.[0]?.measurement || m.menuUoM || customSize || '',
    }],
  }));
}

/** Editable product links for an existing recipe item. */
export default function EditCustomPosItems({ menuItems, updateMenuItems, customSize }) {
  const { data: products = [], load } = useFetch('api/menuItemsRecipes', false);
  const [rows, setRows] = useState(() => normalize(menuItems, customSize));

  const uomChoices = useMemo(() => {
    const map = UoMMenuOptions(customSize);
    if (!map || typeof map !== 'object') return [customSize || 'oz'].filter(Boolean);
    const list = Object.values(map)[0];
    return Array.isArray(list) ? list : [customSize || 'oz'];
  }, [customSize]);

  const options = useMemo(
    () => (Array.isArray(products) ? products : []),
    [products],
  );

  const push = (next) => {
    setRows(next);
    updateMenuItems?.(next);
  };

  const setProduct = (index, selected) => {
    const next = rows.map((row, i) => {
      if (i !== index) return row;
      if (!selected) return emptyRow(customSize);
      return {
        ...row,
        name: selected.posMenuItem,
        variant: selected.posMenuItemVariant || 'Modifier',
        menuId: selected.posMenuItemId,
        posId: selected.posItemId,
      };
    });
    push(next);
  };

  const setAmount = (index, amount) => {
    const next = rows.map((row, i) => {
      if (i !== index) return row;
      const parsed = amount === '' ? '' : Number(amount);
      return {
        ...row,
        menuAmount: parsed,
        units: [{
          amount: parsed === '' ? null : parsed,
          measurement: row.menuUoM || row.units[0]?.measurement || customSize || '',
        }],
      };
    });
    push(next);
  };

  const setUom = (index, uom) => {
    const next = rows.map((row, i) => {
      if (i !== index) return row;
      return {
        ...row,
        menuUoM: uom,
        units: [{
          amount: row.menuAmount === '' ? null : Number(row.menuAmount),
          measurement: uom,
        }],
      };
    });
    push(next);
  };

  const addRow = () => push([...rows, emptyRow(customSize)]);
  const removeRow = (index) => push(rows.filter((_, i) => i !== index));

  const selectedOption = (row) => {
    if (row.menuId == null) return null;
    return options.find((p) => p.posMenuItemId === row.menuId) || {
      posMenuItem: row.name,
      posMenuItemVariant: row.variant,
      posMenuItemId: row.menuId,
      posItemId: row.posId,
    };
  };

  return (
    <Stack spacing={1.25}>
      {rows.map((row, index) => (
        <Stack
          key={`edit-pos-${row.menuId ?? 'new'}-${index}`}
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1}
          alignItems={{ sm: 'center' }}
          sx={{
            p: 1.25,
            borderRadius: 2,
            border: `1px solid ${tokens.border}`,
            backgroundColor: tokens.bg,
          }}
        >
          <Autocomplete
            fullWidth
            size="small"
            loading={load}
            options={options}
            value={selectedOption(row)}
            getOptionLabel={optionLabel}
            isOptionEqualToValue={(a, b) => a?.posMenuItemId === b?.posMenuItemId}
            onChange={(_, value) => setProduct(index, value)}
            renderInput={(params) => (
              <TextField {...params} label="Product" placeholder="Select product" />
            )}
            sx={{ flex: 2, minWidth: 0 }}
          />
          <TextField
            size="small"
            type="number"
            label="Amount used"
            inputProps={{ min: 0, step: 'any' }}
            value={row.menuAmount ?? ''}
            onChange={(e) => setAmount(index, e.target.value)}
            sx={{ width: { xs: '100%', sm: 120 }, flexShrink: 0 }}
          />
          <TextField
            select
            size="small"
            label="UoM"
            value={row.menuUoM || ''}
            disabled={row.menuAmount === '' || row.menuAmount == null}
            onChange={(e) => setUom(index, e.target.value)}
            sx={{ width: { xs: '100%', sm: 110 }, flexShrink: 0 }}
          >
            {uomChoices.map((u) => (
              <MenuItem key={u} value={u}>{u}</MenuItem>
            ))}
          </TextField>
          <IconButton
            aria-label="Remove product"
            onClick={() => removeRow(index)}
            sx={{ color: tokens.danger, alignSelf: { xs: 'flex-end', sm: 'center' } }}
          >
            <DeleteOutlineIcon fontSize="small" />
          </IconButton>
        </Stack>
      ))}

      <Box>
        <Button
          size="small"
          startIcon={<AddOutlinedIcon />}
          onClick={addRow}
          sx={{ color: tokens.accent }}
        >
          Add product
        </Button>
        {rows.length === 0 && (
          <Typography variant="caption" sx={{ display: 'block', color: tokens.inkMuted, mt: 0.5 }}>
            Optional — link finished products that consume this recipe item.
          </Typography>
        )}
      </Box>
    </Stack>
  );
}
